// pages/AuctionListPage.js
import { Clock, DollarSign, Gavel, Users } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from '../utils/axiosHandler';

const AuctionListPage = () => {
    const [auctions, setAuctions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        fetchAuctions();
    }, []);

    const fetchAuctions = async () => {
        try {
            const response = await axios.get('/api/auctions/public/all');

            if (response.status === 200) {
                const data = response.data;
                setAuctions(data);
            } else {
                setError('Error loading auctions');
            }
        } catch (error) {
            console.error('Error fetching auctions:', error);
            setError('Error loading auctions');
        } finally {
            setLoading(false);
        }
    };

    const formatTimeRemaining = (endDate) => {
        const endTime = new Date(endDate).getTime();
        const now = new Date().getTime();
        const remaining = Math.max(0, endTime - now);

        if (remaining <= 0) return 'Auction ended';

        const days = Math.floor(remaining / (1000 * 60 * 60 * 24));
        const hours = Math.floor((remaining % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));

        if (days > 0) return `${days} days, ${hours} hours`;
        return `${hours} hours`;
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-center">
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">Error Loading Auctions</h2>
                    <p className="text-gray-600 mb-4">{error}</p>
                    <button
                        onClick={fetchAuctions}
                        className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                    >
                        Try Again
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 py-8">
            <div className="max-w-6xl mx-auto px-4">
                <div className="text-center mb-8">
                    <h1 className="text-4xl font-bold text-gray-900 mb-4">Live Auctions</h1>
                    <p className="text-xl text-gray-600">Discover exclusive items available for bidding</p>
                </div>

                {auctions.length === 0 ? (
                    <div className="text-center py-12">
                        <Gavel className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                        <h2 className="text-2xl font-bold text-gray-900 mb-2">No Auctions Available</h2>
                        <p className="text-gray-600">Check back later for new auction opportunities.</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {auctions.map(auction => (
                            <div key={auction._id} className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                                <div className="p-6">
                                    <div className="flex items-center justify-between mb-4">
                                        <h3 className="text-xl font-bold text-gray-900">{auction.title}</h3>
                                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${auction.status === 'active' ? 'bg-green-100 text-green-800' :
                                            auction.status === 'draft' ? 'bg-yellow-100 text-yellow-800' :
                                                'bg-gray-100 text-gray-800'
                                            }`}>
                                            {auction.status.toUpperCase()}
                                        </span>
                                    </div>

                                    <p className="text-gray-600 mb-4 line-clamp-3">{auction.description}</p>

                                    <div className="grid grid-cols-2 gap-4 mb-4">
                                        <div className="text-center p-3 bg-blue-50 rounded">
                                            <DollarSign className="w-5 h-5 mx-auto text-blue-600 mb-1" />
                                            <div className="text-sm text-gray-600">Min Bid</div>
                                            <div className="font-semibold">${auction.minimumBid.toLocaleString()}</div>
                                        </div>
                                        <div className="text-center p-3 bg-green-50 rounded">
                                            <DollarSign className="w-5 h-5 mx-auto text-green-600 mb-1" />
                                            <div className="text-sm text-gray-600">Buy Now</div>
                                            <div className="font-semibold">${auction.maximumBid.toLocaleString()}</div>
                                        </div>
                                    </div>

                                    <div className="flex items-center justify-between text-sm text-gray-600 mb-4">
                                        <span className="flex items-center gap-1">
                                            <Clock className="w-4 h-4" />
                                            {formatTimeRemaining(auction.endDate)}
                                        </span>
                                        <span className="flex items-center gap-1">
                                            <Users className="w-4 h-4" />
                                            {auction.currentHighestBid > 0 ? `$${auction.currentHighestBid.toLocaleString()}` : 'No bids'}
                                        </span>
                                    </div>

                                    <Link
                                        to={`/auction/${auction._id}`}
                                        className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 font-medium text-center block transition-colors"
                                    >
                                        View Auction
                                    </Link>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default AuctionListPage; 